import packageJson from '../package.json';

import {
  DEV_ENV_CONFIGS,
  getSelectedDevEnv,
} from './constants/devEnvironments';
import { localEnvironment } from './constants/localEnvironment';

export enum ConfigKeys {
  AiApiUrl = 'aiApiUrl',
  ApiUrl = 'apiUrl',
  Environment = 'environment',
  GitHash = 'gitHash',
  MuiLicenseKey = 'muiLicenseKey',
  ProdApiUrl = 'prodApiUrl',
  SentryAuthToken = 'sentryAuthToken',
  SentryDSN = 'SentryDSN',
  SgwUrl = 'sgwUrl',
  StagingApiUrl = 'stagingApiUrl',
  Version = 'version',
}

/**
 * A TypeScript object with all config information,
 * to make it easier to get the desired env value.
 *
 * @property {string} aiApiUrl - The base URL of the AI API for the current environment.
 * @property {string} apiUrl - The base URL of the API for the current environment.
 * @property {string} environment - The environment in which the app is running.
 * @property {string} gitHash - The git hash of the current commit.
 * @property {string} muiLicenseKey - The key for the Material UI license.
 * @property {string} prodApiUrl - The base URL of the production API.
 * @property {string} sentryAuthToken - The sentry auth token for the vite plugin
 * @property {string} sentryDSN - The base sentry dsn url
 * @property {string} sgwUrl - The base URL of the SGW.
 * @property {string} stagingApiUrl - The base URL of the staging API.
 * @property {string} version - The current version of the app from the package.json
 */
const isDev = import.meta.env.DEV;
const devOverride = isDev ? DEV_ENV_CONFIGS[getSelectedDevEnv()] : null;

export const PROD_API_URL = 'https://app.vestigas.com/api/v1';

export const Config: Record<ConfigKeys, string> = {
  [ConfigKeys.AiApiUrl]:
    devOverride?.aiApiUrl ?? import.meta.env.VITE_AI_API_URL ?? '',
  [ConfigKeys.ApiUrl]:
    localEnvironment.VITE_LOCAL_API_URL ??
    devOverride?.apiUrl ??
    import.meta.env.VITE_API_URL ??
    '',
  [ConfigKeys.Environment]: import.meta.env.MODE,
  [ConfigKeys.GitHash]: import.meta.env.VITE_GIT_HASH,
  [ConfigKeys.MuiLicenseKey]: import.meta.env.VITE_MUI_LICENSE_KEY ?? '',
  // Overridable in dev builds only, so cross-env features can be tested against a local stand-in.
  [ConfigKeys.ProdApiUrl]:
    (isDev ? import.meta.env.VITE_PROD_API_URL : undefined) ?? PROD_API_URL,
  [ConfigKeys.SentryAuthToken]: import.meta.env.VITE_SENTRY_AUTH_TOKEN ?? '',
  [ConfigKeys.SentryDSN]: import.meta.env.VITE_SENTRY_DSN ?? '',
  [ConfigKeys.SgwUrl]:
    devOverride?.sgwUrl ?? import.meta.env.VITE_SGW_URL ?? '',
  [ConfigKeys.StagingApiUrl]: 'https://app.staging.vestigas.com/api/v1',
  [ConfigKeys.Version]: packageJson.version ?? '',
};
